import { DesignData, PageStepDirection } from '../types';

import getPageCountStep from './getPageCountStep';
import getSpreadCountForIncrement, { getNeighbourPageCount } from './getSpreadCountForIncrement';
import resolveTemplateSpreadIndex from './resolveTemplateSpreadIndex';

// The min/max/increment window a multipage product's page count must stay
// within, merged from the design's page_count_limit and (when sold online) the
// ecommerce variant grid — see usePageCountLimit.
export interface PageCountLimit {
  min: number;
  max: number;
  increment?: number;
  // The sold page counts inside [min, max], ascending, when the product is sold online.
  pageCounts?: number[];
}

// The window the buttons gate on: the page counts the ecommerce product sells for the current
// selection, kept to those the design's own limits allow. Either side alone is used as is.
export function mergePageCountLimits(
  designLimit: PageCountLimit | undefined,
  ecommercePageCounts: number[] | undefined,
): PageCountLimit | undefined {
  if (!ecommercePageCounts?.length) {
    return designLimit;
  }

  const pageCounts = designLimit
    ? ecommercePageCounts.filter((count) => count >= designLimit.min && count <= designLimit.max)
    : ecommercePageCounts;
  const soldMin = ecommercePageCounts[0];
  const soldMax = ecommercePageCounts[ecommercePageCounts.length - 1];
  return {
    min: pageCounts.length ? pageCounts[0] : Math.max(designLimit?.min ?? soldMin, soldMin),
    max: pageCounts.length ? pageCounts[pageCounts.length - 1] : Math.min(designLimit?.max ?? soldMax, soldMax),
    increment: designLimit?.increment,
    pageCounts,
  };
}

// Whether the design may be put on `pageCount`: inside the window and on its grid, i.e. one of the
// sold page counts when sold online, else min + k * increment.
export function isAllowedPageCount(pageCount: number, pageCountLimit: PageCountLimit | undefined): boolean {
  if (!pageCountLimit || !Number.isInteger(pageCount)) {
    return false;
  }
  if (pageCount < pageCountLimit.min || pageCount > pageCountLimit.max) {
    return false;
  }
  if (pageCountLimit.pageCounts) {
    return pageCountLimit.pageCounts.includes(pageCount);
  }
  const step = pageCountLimit.increment && pageCountLimit.increment > 0 ? pageCountLimit.increment : 1;
  return (pageCount - pageCountLimit.min) % step === 0;
}

// Whether one add / remove step may take the design from its page count to `target`: inside the
// window always, and on the grid as long as the design is on it now (a template that opens off the
// grid keeps the window alone, or it could never move).
export function canStepToPageCount(designData: DesignData, pageCountLimit: PageCountLimit, target: number): boolean {
  if (target < pageCountLimit.min || target > pageCountLimit.max) {
    return false;
  }
  const current = designData.page_count;
  return (
    current === undefined || !isAllowedPageCount(current, pageCountLimit) || isAllowedPageCount(target, pageCountLimit)
  );
}

// Where one add / remove click takes the page count, or undefined when it may not move. Sold
// online, a click jumps to the neighbouring sold page count, so that neighbour is what has to fit
// (and, given a spread, be reachable in whole spreads, sized as the operations size them).
// Otherwise a click moves a whole grid step (see getPageCountStep).
function getSteppedPageCount(
  designData: DesignData,
  pageCountLimit: PageCountLimit,
  direction: PageStepDirection,
  spreadIndex?: number,
): number | undefined {
  const pageCount = designData.page_count ?? 0;
  let target: number | undefined;
  if (pageCountLimit.pageCounts) {
    target = getNeighbourPageCount(pageCount, pageCountLimit.pageCounts, direction);
    if (target !== undefined && spreadIndex !== undefined) {
      const stepSpreadIndex =
        direction === 'add' ? resolveTemplateSpreadIndex(designData, spreadIndex) ?? spreadIndex : spreadIndex;
      if (!getSpreadCountForIncrement(designData, stepSpreadIndex, direction, pageCountLimit.pageCounts)) {
        return undefined;
      }
    }
  } else if (spreadIndex !== undefined) {
    // Exactly the step the operation takes: as many spreads as it adds or removes, sized as the
    // spread it copies or removes, so a click near either end cannot pass it or land off the grid.
    const stepSpreadIndex =
      direction === 'add' ? resolveTemplateSpreadIndex(designData, spreadIndex) ?? spreadIndex : spreadIndex;
    const step =
      getSpreadCountForIncrement(designData, stepSpreadIndex, direction) *
      (designData.spreads[stepSpreadIndex]?.pages.length || 1);
    target = direction === 'add' ? pageCount + step : pageCount - step;
    return canStepToPageCount(designData, pageCountLimit, target) ? target : undefined;
  } else {
    const step = getPageCountStep(designData, pageCountLimit.increment);
    target = direction === 'add' ? pageCount + step : pageCount - step;
  }

  if (target === undefined || target < pageCountLimit.min || target > pageCountLimit.max) {
    return undefined;
  }
  return target;
}

// The helpers below are the single source of truth for when the add / duplicate / remove controls
// are enabled — shared by the top-menu buttons, the per-spread thumbnail menu and the mobile list.

export function canAddPagesForSpread(
  designData: DesignData | undefined,
  canAddAfter: boolean,
  pageCountLimit: PageCountLimit | undefined,
  spreadIndex: number | undefined,
): boolean {
  if (!designData || !pageCountLimit || !canAddAfter) {
    return false;
  }
  return getSteppedPageCount(designData, pageCountLimit, 'add', spreadIndex) !== undefined;
}

export function canRemovePagesForSpread(
  designData: DesignData | undefined,
  canRemove: boolean,
  pageCountLimit: PageCountLimit | undefined,
  spreadIndex: number | undefined,
): boolean {
  if (!designData || !pageCountLimit || !canRemove) {
    return false;
  }
  return getSteppedPageCount(designData, pageCountLimit, 'remove', spreadIndex) !== undefined;
}

// Whether adding would pass the maximum page count, regardless of the spread's own canAddAfter
// flag. Drives the "can't add pages" tooltip.
export function isAtMaxPageCount(
  designData: DesignData | undefined,
  pageCountLimit: PageCountLimit | undefined,
): boolean {
  if (!designData || !pageCountLimit) {
    return false;
  }
  return getSteppedPageCount(designData, pageCountLimit, 'add') === undefined;
}

// Whether removing would drop below the minimum page count, regardless of the spread's own
// canRemove flag. Drives the "can't delete pages" tooltip.
export function isAtMinPageCount(
  designData: DesignData | undefined,
  pageCountLimit: PageCountLimit | undefined,
): boolean {
  if (!designData || !pageCountLimit) {
    return false;
  }
  return getSteppedPageCount(designData, pageCountLimit, 'remove') === undefined;
}
